/**
 * Creative content type discriminants.
 *
 * These mirror the Rust TagBidContent enum variants serialised via
 * `#[serde(tag = "type", rename_all = "snake_case")]`. The wire value
 * of the `type` field must match exactly — change here and in Rust in tandem.
 *
 * BANNER — HTML display markup with declared pixel dimensions (w, h).
 *          Rendered via sandboxed srcdoc iframe (non-expandable) or direct
 *          inject (expandable). w/h are the declared initial creative size;
 *          when expandable=true the creative's own CSS controls final sizing.
 *
 * VIDEO  — VAST XML with declared pixel dimensions (w, h).
 *          Requires a VAST player — not yet rendered in tag v1.
 *
 * AUDIO  — VAST XML, no dimensions.
 *          Requires a VAST player — not yet rendered in tag v1.
 */
export const CONTENT_TYPE_BANNER = 'banner';
export const CONTENT_TYPE_VIDEO  = 'video';
export const CONTENT_TYPE_AUDIO  = 'audio';

/**
 * Creative content delivered to the tag for rendering.
 * Mirrors Rust TagBidContent — discriminated on the `type` field.
 */
export class TagBidContent {
  /** @param {{ type: string } & Record<string, unknown>} raw */
  constructor(raw) {
    switch (raw.type) {
      case CONTENT_TYPE_BANNER:
        this._type    = CONTENT_TYPE_BANNER;
        this._content = raw.content;
        this._w       = raw.w;
        this._h       = raw.h;
        break;

      case CONTENT_TYPE_VIDEO:
        this._type = CONTENT_TYPE_VIDEO;
        this._vast = raw.vast;
        this._w    = raw.w;
        this._h    = raw.h;
        break;

      case CONTENT_TYPE_AUDIO:
        this._type = CONTENT_TYPE_AUDIO;
        this._vast = raw.vast;
        break;

      default:
        throw new Error(`Unknown TagBidContent type: ${raw.type}`);
    }
  }

  /**
   * Content type discriminant. Compare against CONTENT_TYPE_* constants.
   * @returns {'banner' | 'video' | 'audio'}
   */
  get type() { return this._type; }

  /**
   * HTML markup string. Only present when type is CONTENT_TYPE_BANNER.
   * @returns {string | undefined}
   */
  get content() { return this._content; }

  /**
   * VAST XML string. Only present when type is CONTENT_TYPE_VIDEO or CONTENT_TYPE_AUDIO.
   * @returns {string | undefined}
   */
  get vast() { return this._vast; }

  /**
   * Declared creative width in pixels.
   * Only present when type is CONTENT_TYPE_BANNER or CONTENT_TYPE_VIDEO.
   * For expandable banners this is the initial declared size — the creative's
   * CSS controls final rendered dimensions after expansion.
   * @returns {number | undefined}
   */
  get w() { return this._w; }

  /**
   * Declared creative height in pixels.
   * Only present when type is CONTENT_TYPE_BANNER or CONTENT_TYPE_VIDEO.
   * For expandable banners this is the initial declared size — the creative's
   * CSS controls final rendered dimensions after expansion.
   * @returns {number | undefined}
   */
  get h() { return this._h; }
}
